/// <reference types="vite/client" />
import { createClient, SupabaseClient } from '@supabase/supabase-js';
import type { SupabaseClientOptions } from '@supabase/supabase-js';
import type { Database } from './types';

let supabaseInstance: SupabaseClient<Database, 'public', 'public'> | null = null;

// Token provider function - will be set by Clerk integration in admin app
let tokenProvider: (() => Promise<string | null>) | null = null;

/**
 * Set a custom token provider for Clerk integration
 * This allows Clerk's session token to be injected into Supabase requests
 * 
 * @param provider - Async function that returns Clerk session token
 */
export function setSupabaseTokenProvider(provider: () => Promise<string | null>) {
	tokenProvider = provider;
	// Reset instance to recreate with new token provider
	supabaseInstance = null;
	// Force recreation of the client with the new token provider
	getSupabaseClient();
}

/**
 * CLIENT-SIDE ONLY: Singleton Supabase client for React applications
 * 
 * This client is shared across all React apps in the monorepo (admin, customers, business).
 * It handles authentication, session persistence, and automatic token refresh for browser environments.
 * 
 * For Clerk-authenticated apps (admin), the token provider is set via setSupabaseTokenProvider()
 * to inject Clerk JWT tokens into Supabase requests for RLS policies.
 * 
 * For Supabase Edge Functions (Deno runtime), use createSupabaseClient from:
 * supabase/functions/_shared/supabaseEdgeClient.ts
 * 
 * @requires Environment variables:
 * - Next.js apps: NEXT_PUBLIC_SUPABASE_URL and NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY
 * - Vite apps: VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY
 * 
 * @returns Singleton SupabaseClient instance with Database types
 */
export function getSupabaseClient(): SupabaseClient<Database, 'public', 'public'> {
	if (!supabaseInstance) {
		// Support both Next.js (process.env.NEXT_PUBLIC_*) and Vite (import.meta.env.VITE_*) environments
		const supabaseUrl = 
			(typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_SUPABASE_URL) ||
			(typeof import.meta !== 'undefined' && import.meta.env?.VITE_SUPABASE_URL);
		
		const supabaseKey = 
			(typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY) ||
			(typeof import.meta !== 'undefined' && import.meta.env?.VITE_SUPABASE_PUBLISHABLE_KEY);

		if (!supabaseUrl || !supabaseKey) {
			throw new Error(
				'Supabase environment variables are required. ' +
				'For Next.js: NEXT_PUBLIC_SUPABASE_URL and NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY. ' +
				'For Vite: VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY'
			);
		}

		const clientOptions: SupabaseClientOptions<'public'> = {
			auth: {
				persistSession: false, // Clerk manages sessions, not Supabase
				autoRefreshToken: false,
				detectSessionInUrl: false,
			},
		};

		// If a token provider is set (Clerk), inject auth token into all requests
		if (tokenProvider) {
			const provider = tokenProvider; // Capture for closure
			// Use accessToken callback - this is the correct way for Supabase v2
			clientOptions.accessToken = async () => {
				const token = await provider();
				return token;
			};
		} else {
			// Standard Supabase auth for non-Clerk apps
			clientOptions.auth = {
				persistSession: true,
				autoRefreshToken: true,
				detectSessionInUrl: true,
				storage: globalThis.window === undefined ? undefined : globalThis.window.localStorage,
				storageKey: 'supabase.auth.token',
				flowType: 'pkce',
			};
		}

		supabaseInstance = createClient<Database, 'public', 'public'>(supabaseUrl, supabaseKey, clientOptions);
	}

	return supabaseInstance;
}

/**
 * Default export for convenience in React components
 * Returns the current Supabase client instance
 * 
 * @example
 * ```typescript
 * import { supabase } from '@cleanpro/common-ui'
 * await supabase.from('customers').select('*')
 * ```
 */
export const supabase = new Proxy({} as SupabaseClient<Database, 'public', 'public'>, {
	get(_target, prop) {
		const client = getSupabaseClient();
		const value = (client as SupabaseClient<Database, 'public', 'public'>)[prop as keyof SupabaseClient<Database, 'public', 'public'>];
		return typeof value === 'function' ? value.bind(client) : value;
	}
});
